import { DetailForm } from '@/@diffrag/form/DetailForm'
import { FormFieldData, FormFieldType } from '@/@diffrag/form/fields/type'
import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentDetailView } from '@/@diffrag/layout/ContentDetailView'
import { api } from '@/api'
import { Button } from '@/components/ui/button'
import { exportExcel } from '@/lib/excel'
import { toast } from '@/lib/toast'
import {
  IAdminPromotionDetailRO,
  IAdminPromotionDetailRoStatusEnum,
  IAdminPromotionSave,
  IAdminPromotionSaveTypeEnum,
} from 'api'
import dayjs from 'dayjs'
import { Download } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormContext } from 'react-hook-form'

const MEDICAL_PACKAGE_CATEGORY_ID = 100

export default function PromotionEdit() {
  const navigate = useNavigate()
  const id = useQueryValue('id', 0)

  const [categoryId, setCategoryId] = useState<number | undefined>()

  const { data, refetch } = useApi({
    id: 'promotionDetail',
    fn: api().adminPromotion.adminPromotionDetail,
    params: Number(id),
    enabled: !!id,
  })

  // 페이지 진입 시 data.category.id를 초기값으로 설정
  useEffect(() => {
    if (data?.category?.id) {
      setCategoryId(data.category.id)
    }
  }, [data?.category?.id])

  const onSave = async (data: IAdminPromotionDetailRO) => {
    try {
      if (
        data.endDate &&
        data.startDate &&
        dayjs(data.endDate).isBefore(dayjs(data.startDate))
      ) {
        alert('종료일이 시작일보다 이전입니다.')
        return
      }

      const imageId = (data as { image?: { id: number } }).image?.id || 0

      if (!imageId) {
        alert('썸네일을 선택해주세요.')
        return
      }

      const clinicId =
        categoryId === MEDICAL_PACKAGE_CATEGORY_ID
          ? 0
          : (data as { clinic?: { id: number } }).clinic?.id || 0

      if (categoryId !== MEDICAL_PACKAGE_CATEGORY_ID && !clinicId) {
        alert('병원을 선택해주세요.')
        return
      }

      const saveData: IAdminPromotionSave = {
        type: data.type as unknown as IAdminPromotionSaveTypeEnum,
        imageId,
        imageZhcnId: (data as { imageZhcn?: { id: number } }).imageZhcn?.id,
        imageZhtwId: (data as { imageZhtw?: { id: number } }).imageZhtw?.id,
        imageEnId: (data as { imageEn?: { id: number } }).imageEn?.id,
        imageJaId: (data as { imageJa?: { id: number } }).imageJa?.id,
        imageThId: (data as { imageTh?: { id: number } }).imageTh?.id,
        categoryId: data.category.id,
        clinicId,
        startDate: data.startDate || '',
        endDate: data.endDate || '',
        title: data.title,
        titleZhcn: data.titleZhcn || undefined,
        titleZhtw: data.titleZhtw || undefined,
        titleEn: data.titleEn || undefined,
        titleJa: data.titleJa || undefined,
        titleTh: data.titleTh || undefined,
        content: data.content || '',
        contentZhcn: data.contentZhcn || undefined,
        contentZhtw: data.contentZhtw || undefined,
        contentEn: data.contentEn || undefined,
        contentJa: data.contentJa || undefined,
        contentTh: data.contentTh || undefined,
        discountRate: data.discountRate || undefined,
        originalPrice: data.originalPrice || undefined,
        discountPrice: data.discountPrice || undefined,
        tags: [
          ...(data.hashtagsKo || []).map((hashtag, index) => ({
            hashtag,
            language: 'ko',
            order: index,
          })),
          ...(data.hashtagsZhcn || []).map((hashtag, index) => ({
            hashtag,
            language: 'zh-CN',
            order: index,
          })),
          ...(data.hashtagsZhtw || []).map((hashtag, index) => ({
            hashtag,
            language: 'zh-TW',
            order: index,
          })),
          ...(data.hashtagsEn || []).map((hashtag, index) => ({
            hashtag,
            language: 'en',
            order: index,
          })),
          ...(data.hashtagsJa || []).map((hashtag, index) => ({
            hashtag,
            language: 'ja',
            order: index,
          })),
          ...(data.hashtagsTh || []).map((hashtag, index) => ({
            hashtag,
            language: 'th',
            order: index,
          })),
        ] as never,
      }

      if (id) {
        await api().adminPromotion.adminPromotionUpdate(Number(id), saveData)
        refetch()
      } else {
        const res = await api().adminPromotion.adminPromotionCreate(saveData)
      }

      navigate(-1)

      toast.success('저장되었습니다.')
    } catch (error) {
      console.error(error)

      // 에러 상세 정보 추출
      let errorMessage = '알 수 없는 오류가 발생했습니다.'
      let errorDetails = ''

      if (error instanceof Error) {
        errorMessage = error.message || errorMessage
        errorDetails = error.stack || ''
      } else if (error && typeof error === 'object') {
        // Response 타입 체크
        if ('error' in error && error.error) {
          const errorObj = error.error as { message?: string }
          if (errorObj?.message) {
            errorMessage = errorObj.message
          }
        } else if ('message' in error) {
          errorMessage = String(error.message)
        }

        // 전체 에러 객체를 JSON으로 변환
        try {
          errorDetails = JSON.stringify(error, null, 2)
        } catch {
          errorDetails = String(error)
        }
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      // 상세 에러 정보를 alert으로 표시
      const fullErrorMessage = errorDetails
        ? `${errorMessage}\n\n상세 정보:\n${errorDetails}`
        : errorMessage

      alert(`저장 중 오류가 발생했습니다.\n\n${fullErrorMessage}`)
    }
  }

  const handleExportParticipants = () => {
    if (!data?.participants || data.participants.length === 0) {
      alert('다운로드할 참여자가 없습니다.')
      return
    }

    const columns = [
      { id: 'name', header: '이름', accessorKey: 'name' },
      { id: 'email', header: '이메일', accessorKey: 'email' },
      { id: 'clinicName', header: '병원명', accessorKey: 'clinicName' },
      { id: 'categoryName', header: '관심분야', accessorKey: 'categoryName' },
      { id: 'createDate', header: '신청일', accessorKey: 'createDate' },
      {
        id: 'agreePrivacyDate',
        header: '개인정보동의일',
        accessorKey: 'agreePrivacyDate',
      },
    ]

    const formattedData = data.participants.map((participant) => ({
      name: participant.name || '-',
      email: participant.email || '-',
      clinicName: participant.clinicName || '-',
      categoryName: participant.category.name || '-',
      createDate: participant.createDate
        ? dayjs(participant.createDate).format('YYYY-MM-DD HH:mm')
        : '-',
      agreePrivacyDate: participant.agreePrivacyDate
        ? dayjs(participant.agreePrivacyDate).format('YYYY-MM-DD HH:mm')
        : '-',
    }))

    exportExcel(formattedData, columns, `${data.title}_참여자목록`)
  }

  const baseFields: FormFieldData<IAdminPromotionDetailRO>[] = useMemo(
    () => [
      {
        label: '카테고리',
        key: 'category',
        type: FormFieldType.RELATION,
        searchApiFn: async () => {
          const result = await api().adminCategory.adminCategoryList({})
          return {
            rows: result.items,
            page: 1,
            total: result.items?.length || 0,
          }
        },
        onAdd: (data) => {
          setCategoryId(data.id)
        },
        onRemove: () => {
          setCategoryId(undefined)
        },
        labelKey: 'name',
        required: true,
      },
      {
        label: '병원선택',
        key: 'clinic',
        type: FormFieldType.RELATION,
        searchApiFn: async ({ searchText, amount, page }) => {
          const result = await api().adminClinic.adminClinicList({
            page: page || 1,
            q: searchText || '',
            categoryIds: categoryId ? [categoryId] : [],
            regionIds: [],
          })

          const clinics = result.users.map((user) => user.clinic)

          return {
            rows: clinics,
            page: page || 1,
            total: result.total || 0,
          }
        },

        labelKey: 'name',
      },
      {
        label: '타입',
        key: 'type',
        type: FormFieldType.SELECT,
        options: [
          { label: '이벤트', value: 'EVENT' },
          { label: '한정프로모션', value: 'LIMITED' },
          { label: '메디컬패키지', value: 'MEDICAL_PACKAGE' },
        ],
        required: true,
      },
      {
        label: '시작일',
        key: 'startDate',
        type: FormFieldType.DATE,
        required: true,
      },
      {
        label: '종료일',
        key: 'endDate',
        type: FormFieldType.DATE,
        required: true,
      },
      {
        label: '프로모션명 (한국어)',
        key: 'title',
        type: FormFieldType.STRING,
        required: true,
      },
      {
        label: '프로모션명 (简体中文)',
        key: 'titleZhcn',
        type: FormFieldType.STRING,
      },
      {
        label: '프로모션명 (繁體中文)',
        key: 'titleZhtw',
        type: FormFieldType.STRING,
      },
      {
        label: '프로모션명 (English)',
        key: 'titleEn',
        type: FormFieldType.STRING,
      },
      {
        label: '프로모션명 (日本語)',
        key: 'titleJa',
        type: FormFieldType.STRING,
      },
      {
        label: '프로모션명 (ไทย)',
        key: 'titleTh',
        type: FormFieldType.STRING,
      },
      {
        label: '시술내용 (한국어)',
        key: 'content',
        type: FormFieldType.CKEDITOR,
        required: true,
        imageAlertText: '이미지 권장 사이즈 : 가로 최소 800',
      },
      {
        label: '시술내용 (简体中文)',
        key: 'contentZhcn',
        type: FormFieldType.CKEDITOR,
        imageAlertText: '이미지 권장 사이즈 : 가로 최소 800',
      },
      {
        label: '시술내용 (繁體中文)',
        key: 'contentZhtw',
        type: FormFieldType.CKEDITOR,
        imageAlertText: '이미지 권장 사이즈 : 가로 최소 800',
      },
      {
        label: '시술내용 (English)',
        key: 'contentEn',
        type: FormFieldType.CKEDITOR,
        imageAlertText: '이미지 권장 사이즈 : 가로 최소 800',
      },
      {
        label: '시술내용 (日本語)',
        key: 'contentJa',
        type: FormFieldType.CKEDITOR,
        imageAlertText: '이미지 권장 사이즈 : 가로 최소 800',
      },
      {
        label: '시술내용 (ไทย)',
        key: 'contentTh',
        type: FormFieldType.CKEDITOR,
        imageAlertText: '이미지 권장 사이즈 : 가로 최소 800',
      },
      {
        label: '금액',
        key: 'originalPrice',
        type: FormFieldType.NUMBER,
      },
      {
        label: '할인율 (%)',
        key: 'discountRate',
        type: FormFieldType.NUMBER,
        required: true,
      },
      {
        label: '할인금액',
        key: 'discountPrice',
        type: FormFieldType.NUMBER,
      },
      {
        label: '시술분야 (한국어)',
        key: 'hashtagsKo',
        type: FormFieldType.HASHTAG,
        default:
          data?.hashtags
            ?.filter((v) => v.language === 'ko')
            .map((v) => v.hashtag) || [],
      },
      {
        label: '시술분야 (简体中文)',
        key: 'hashtagsZhcn',
        type: FormFieldType.HASHTAG,
        default:
          data?.hashtags
            ?.filter((v) => v.language === 'zh-CN')
            .map((v) => v.hashtag) || [],
      },
      {
        label: '시술분야 (繁體中文)',
        key: 'hashtagsZhtw',
        type: FormFieldType.HASHTAG,
        default:
          data?.hashtags
            ?.filter((v) => v.language === 'zh-TW')
            .map((v) => v.hashtag) || [],
      },
      {
        label: '시술분야 (English)',
        key: 'hashtagsEn',
        type: FormFieldType.HASHTAG,
        default:
          data?.hashtags
            ?.filter((v) => v.language === 'en')
            .map((v) => v.hashtag) || [],
      },
      {
        label: '시술분야 (日本語)',
        key: 'hashtagsJa',
        type: FormFieldType.HASHTAG,
        default:
          data?.hashtags
            ?.filter((v) => v.language === 'ja')
            .map((v) => v.hashtag) || [],
      },
      {
        label: '시술분야 (ไทย)',
        key: 'hashtagsTh',
        type: FormFieldType.HASHTAG,
        default:
          data?.hashtags
            ?.filter((v) => v.language === 'th')
            .map((v) => v.hashtag) || [],
      },
      {
        label: '썸네일 (한국어)',
        key: 'image',
        type: FormFieldType.FILE,
        accept: 'image/*',
        required: true,
        async uploadHandler(file) {
          const res = await api().assets.uploadCreate({
            file: file.file,
            crop: JSON.stringify([file.crop]),
          })

          return res
        },
        imageAlertText: '이미지 권장 사이즈 : 최소 348*232 / 비율 3:2 또는 4:3',
      },
      {
        label: '썸네일 (简体中文)',
        key: 'imageZhcn',
        type: FormFieldType.FILE,
        accept: 'image/*',
        async uploadHandler(file) {
          const res = await api().assets.uploadCreate({
            file: file.file,
            crop: JSON.stringify([file.crop]),
          })

          return res
        },
        imageAlertText: '이미지 권장 사이즈 : 최소 348*232 / 비율 3:2 또는 4:3',
      },
      {
        label: '썸네일 (繁體中文)',
        key: 'imageZhtw',
        type: FormFieldType.FILE,
        accept: 'image/*',
        async uploadHandler(file) {
          const res = await api().assets.uploadCreate({
            file: file.file,
            crop: JSON.stringify([file.crop]),
          })

          return res
        },
        imageAlertText: '이미지 권장 사이즈 : 최소 348*232 / 비율 3:2 또는 4:3',
      },
      {
        label: '썸네일 (English)',
        key: 'imageEn',
        type: FormFieldType.FILE,
        accept: 'image/*',
        async uploadHandler(file) {
          const res = await api().assets.uploadCreate({
            file: file.file,
            crop: JSON.stringify([file.crop]),
          })

          return res
        },
        imageAlertText: '이미지 권장 사이즈 : 최소 348*232 / 비율 3:2 또는 4:3',
      },
      {
        label: '썸네일 (日本語)',
        key: 'imageJa',
        type: FormFieldType.FILE,
        accept: 'image/*',
        async uploadHandler(file) {
          const res = await api().assets.uploadCreate({
            file: file.file,
            crop: JSON.stringify([file.crop]),
          })

          return res
        },
        imageAlertText: '이미지 권장 사이즈 : 최소 348*232 / 비율 3:2 또는 4:3',
      },
      {
        label: '썸네일 (ไทย)',
        key: 'imageTh',
        type: FormFieldType.FILE,
        accept: 'image/*',
        async uploadHandler(file) {
          const res = await api().assets.uploadCreate({
            file: file.file,
            crop: JSON.stringify([file.crop]),
          })

          return res
        },
        imageAlertText: '이미지 권장 사이즈 : 최소 348*232 / 비율 3:2 또는 4:3',
      },
    ],
    [data?.hashtags],
  )

  // categoryId가 MEDICAL_PACKAGE_CATEGORY_ID일 때 clinic 필드를 제외
  const fields = useMemo(() => {
    if (categoryId === MEDICAL_PACKAGE_CATEGORY_ID) {
      return baseFields.filter((field) => field.key !== 'clinic')
    }
    return baseFields
  }, [baseFields, categoryId])

  // categoryId가 MEDICAL_PACKAGE_CATEGORY_ID일 때 clinic 값을 초기화
  const ClinicResetEffect = () => {
    const form = useFormContext()
    useEffect(() => {
      if (categoryId === MEDICAL_PACKAGE_CATEGORY_ID) {
        form.setValue('clinic', undefined)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId, form])
    return null
  }

  if (id) {
    if (data?.status === 'ENDED') {
      fields.push({
        label: '상태',
        key: 'status',
        type: FormFieldType.STRING,
        readOnly: true,
        resolver(data) {
          return '종료'
        },
      })
    } else {
      fields.push({
        label: '상태',
        key: 'status',
        type: FormFieldType.SELECT,
        options: [
          {
            label: '시작 전',
            value: IAdminPromotionDetailRoStatusEnum.BEFORE_START,
          },
          {
            label: '진행 중',
            value: IAdminPromotionDetailRoStatusEnum.ONGOING,
          },
          { label: '종료', value: IAdminPromotionDetailRoStatusEnum.ENDED },
        ],
      })
    }

    fields.push({
      label: '신청자리스트',
      key: 'participants',
      type: FormFieldType.CUSTOM_READ_ONLY,
      render: (data) => {
        if (!data?.participants || data.participants.length === 0) {
          return <span>신청자가 없습니다.</span>
        }
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">
                총 신청자리스트: {data.participants.length}명
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleExportParticipants}
                className="ml-2 flex items-center gap-2"
              >
                <Download className="size-4" />
                엑셀 다운로드
              </Button>
            </div>
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {data.participants.map((participant, index) => (
                <div key={index} className="rounded border p-3 text-sm">
                  <div className="font-medium">이름: {participant.name}</div>
                  <div className="text-gray-600">
                    이메일: {participant.email}
                  </div>
                  <div className="text-gray-600">
                    병원명: {participant.clinicName}
                  </div>
                  <div className="text-gray-600">
                    관심분야: {participant.category.name}
                  </div>
                  <div className="text-gray-600">
                    신청일:{' '}
                    {dayjs(participant.createDate).format('YYYY-MM-DD HH:mm')}
                  </div>
                  <div className="text-gray-600">
                    개인정보동의일:{' '}
                    {participant.agreePrivacyDate
                      ? dayjs(participant.agreePrivacyDate).format(
                          'YYYY-MM-DD HH:mm',
                        )
                      : '-'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      },
    })
  }

  if (id && !data) {
    return <div>로딩중...</div>
  }

  const onFinish = async () => {
    if (id) {
      confirm('프로모션을 종료하시겠습니까?')
      await api().adminPromotion.finishUpdate(Number(id))
      refetch()
      toast.success('프로모션을 종료하였습니다.')
      navigate(-1)
    }
  }

  return (
    <ContentDetailView
      title={`프로모션 ${id ? '수정' : '등록'}`}
      data={data}
      onSave={onSave}
      hideSaveToast={() => true}
      customButtons={
        data?.status === 'ENDED'
          ? []
          : [
              {
                label: '프로모션 종료',
                onClick: onFinish,
                variant: 'destructive',
                className: 'w-[120px]',
              },
            ]
      }
    >
      <ClinicResetEffect />
      <DetailForm data={data} fields={fields} />
    </ContentDetailView>
  )
}
